import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, Avatar,
  TextField, Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, InputAdornment, MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  EventNote as PeriodsIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, CheckCircle as ActiveIcon,
  Lock as LockIcon, LockOpen as UnlockIcon,
  CalendarMonth as CalIcon, EventAvailable as TermIcon,
  PlayCircleFilled as ActivateIcon, Search as SearchIcon,
  Close as CloseIcon, Check as CheckIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};
const daysBetween = (from, to) => {
  if (!from || !to) return 0;
  const a = new Date(from), b = new Date(to);
  return Math.round((b - a) / 86400000) + 1;
};

// ── Period dialog ──────────────────────────────────────────────
function PeriodDialog({ open, onClose, period, onSave }) {
  const [form, setForm] = useState({ name: '', acc_year_label: '', starts_on: '', ends_on: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (period) {
        setForm({
          name: period.name || '',
          acc_year_label: period.acc_year_label || '',
          starts_on: (period.starts_on || '').slice(0, 10),
          ends_on:   (period.ends_on   || '').slice(0, 10),
        });
      } else {
        const y = new Date().getFullYear();
        setForm({
          name: `${y}/${y + 1} Academic Year`,
          acc_year_label: `${y}/${y + 1}`,
          starts_on: `${y}-09-01`,
          ends_on:   `${y + 1}-08-31`,
        });
      }
      setError('');
    }
  }, [open, period]);

  // Both Academic Year and dates are compulsory — disable Save until provided.
  const canSave = !!(form.name?.trim()
    && form.acc_year_label?.trim()
    && form.starts_on
    && form.ends_on
    && form.starts_on <= form.ends_on);

  const submit = async () => {
    setSaving(true); setError('');
    try { await onSave(form); }
    catch (e) { setError(e?.response?.data?.message || e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          {period ? <EditIcon /> : <AddIcon />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {period ? 'Edit accounting period' : 'New accounting period'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Bind a date range so transactions stay inside it
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField fullWidth size="small" required label="Academic Year"
            placeholder="e.g. 2026/2027"
            value={form.acc_year_label}
            onChange={(e) => setForm({ ...form, acc_year_label: e.target.value })}
            helperText='Mandatory. Stamped on every ledger row (the legacy acc_yer column). Use a short label like "2026/2027".' />
          <TextField fullWidth size="small" required label="Period name (descriptive)"
            placeholder="e.g. 2026/2027 Academic Year"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            helperText="Shown across reports and receipts" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth size="small" required type="date" label="Starts on"
              InputLabelProps={{ shrink: true }}
              value={form.starts_on}
              onChange={(e) => setForm({ ...form, starts_on: e.target.value })} />
            <TextField fullWidth size="small" required type="date" label="Ends on"
              InputLabelProps={{ shrink: true }}
              value={form.ends_on}
              onChange={(e) => setForm({ ...form, ends_on: e.target.value })} />
          </Stack>
          {form.starts_on && form.ends_on && (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
              Academic Year <b>{form.acc_year_label || '—'}</b> spans <b>{daysBetween(form.starts_on, form.ends_on)}</b> day(s).
              Every transaction date must fall inside this window AND inside one of the terms you'll define.
            </Alert>
          )}
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
            <b>Compulsory:</b> after creating the period you <b>must</b> add at least one term.
            Transactions are rejected if the date doesn't fall inside both the academic year and a term.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" color="primary"
          onClick={submit} disabled={saving || !canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
          sx={{ fontWeight: 800, minWidth: 160, textTransform: 'none' }}>
          {saving ? 'Saving…' : (period ? 'Save changes' : 'Create period')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Term dialog ────────────────────────────────────────────────
function TermDialog({ open, onClose, period, term, onSave }) {
  const [form, setForm] = useState({ name: '', starts_on: '', ends_on: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (term) {
        setForm({
          name: term.name || '',
          starts_on: (term.starts_on || '').slice(0, 10),
          ends_on:   (term.ends_on   || '').slice(0, 10),
        });
      } else {
        setForm({
          name: '',
          starts_on: period?.starts_on?.slice(0, 10) || '',
          ends_on:   period?.starts_on?.slice(0, 10) || '',
        });
      }
      setError('');
    }
  }, [open, term, period]);



  const submit = async () => {
    setSaving(true); setError('');
    try { await onSave(form); }
    catch (e) { setError(e?.response?.data?.message || e.message || 'Save failed'); }
    finally { setSaving(false); }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.secondary.main} 0%, ${t.palette.primary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          <TermIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            {term ? 'Edit term' : 'Add term'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {period?.name} · {period?.starts_on} → {period?.ends_on}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField select fullWidth size="small" label="Term name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            helperText="Term name written into every payment / score / register inside this window — must match the canonical system codes (Term_1 / Term_2 / Term_3) so existing records line up.">
            {['Term_1', 'Term_2', 'Term_3'].map(t => (
              <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth size="small" type="date" label="Starts on"
              InputLabelProps={{ shrink: true }}
              value={form.starts_on}
              onChange={(e) => setForm({ ...form, starts_on: e.target.value })} />
            <TextField fullWidth size="small" type="date" label="Ends on"
              InputLabelProps={{ shrink: true }}
              value={form.ends_on}
              onChange={(e) => setForm({ ...form, ends_on: e.target.value })} />
          </Stack>
          {form.starts_on && form.ends_on && (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
              Term spans <b>{daysBetween(form.starts_on, form.ends_on)}</b> day(s).
              Must fit inside the period and not overlap other terms.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" color="secondary"
          onClick={submit} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
          sx={{ fontWeight: 800, minWidth: 160, textTransform: 'none' }}>
          {saving ? 'Saving…' : (term ? 'Save changes' : 'Add term')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Date resolver tool ─────────────────────────────────────────

function ResolverTool() {
  const [date, setDate] = useState(today());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const probe = async (d) => {
    setLoading(true);
    try {
      const r = await API.get('/accounting/resolve', { params: { date: d } });
      setResult(r.data);
    } catch { setResult({ ok: false, error: 'Server error' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { probe(date); }, [date]);

  return (
    <Card sx={{ borderRadius: 2 }}>
      <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
        <SearchIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={800}>Date resolver</Typography>
      </Box>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField size="small" type="date" label="Pick a date"
            InputLabelProps={{ shrink: true }}
            value={date} onChange={(e) => setDate(e.target.value)}
            sx={{ minWidth: 200 }} />
          <Box sx={{ flex: 1 }}>
            {loading && <CircularProgress size={18} />}
            {!loading && result && result.ok && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.6}>
                <Chip color="success" icon={<CheckIcon sx={{ fontSize: 14 }} />}
                  label={`Academic Year: ${result.academic_year || result.acc_yer}`}
                  sx={{ fontWeight: 800 }} />
                <Chip color="primary" icon={<TermIcon sx={{ fontSize: 14 }} />}
                  label={`Term: ${result.term_name}`} sx={{ fontWeight: 800 }} />
                <Chip variant="outlined" label={`Period: ${result.period_name}`}
                  sx={{ fontWeight: 700 }} />
              </Stack>
            )}
            {!loading && result && !result.ok && (
              <Alert severity="error" sx={{ borderRadius: 1.5 }}>{result.error}</Alert>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Active period card ─────────────────────────────────────────
function ActivePeriodCard({ period, terms, onEdit, onLockToggle, onAddTerm, onEditTerm, onDeleteTerm }) {
  if (!period) {
    return (
      <Card sx={{ borderRadius: 2, borderLeft: (t) => `4px solid ${t.palette.error.main}` }}>
        <CardContent sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={800} color="error">No active accounting period</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto', mt: 1 }}>
            Create one below and activate it. Every payment, fee, collection and adjustment requires an active period.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const totalDays = daysBetween(period.starts_on, period.ends_on);
  const t = today();
  const inside = t >= period.starts_on && t <= period.ends_on;
  const elapsed = inside ? Math.max(daysBetween(period.starts_on, t) - 1, 0) : 0;
  const remaining = inside ? Math.max(daysBetween(t, period.ends_on) - 1, 0) : 0;
  const currentTerm = terms.find(x => t >= x.starts_on && t <= x.ends_on);

  return (
    <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2.4,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" rowGap={1}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 50, height: 50 }}>
            <ActiveIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
              <Typography variant="caption" sx={{
                opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.6,
                fontWeight: 800, fontSize: 10,
              }}>
                Academic Year
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ ...TNUM }}>
                {period.acc_year_label || '—'}
              </Typography>
              <Chip size="small" label="ACTIVE" color="success" sx={{ fontWeight: 800 }} />
              {period.locked === 1 || period.locked === true ? (
                <Chip size="small" icon={<LockIcon sx={{ fontSize: 14 }} />}
                  label="LOCKED" color="error" sx={{ fontWeight: 800 }} />
              ) : null}
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.3 }}>
              {period.name} · {fmtDate(period.starts_on)} → {fmtDate(period.ends_on)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.6}>
            <Tooltip title="Edit period">
              <IconButton onClick={() => onEdit(period)}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={period.locked ? 'Unlock period' : 'Lock period (no new transactions)'}>
              <IconButton onClick={() => onLockToggle(period)}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.16)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                {period.locked ? <UnlockIcon fontSize="small" /> : <LockIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
          gap: 1.5, mt: 2,
        }}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Total days
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, ...TNUM }}>{totalDays}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Elapsed
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, ...TNUM }}>{elapsed}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Remaining
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, ...TNUM }}>{remaining}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>
              Current term
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
              {currentTerm ? currentTerm.name : '—'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <TermIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={800}>
            Terms inside this period
          </Typography>
          <Chip size="small" variant="outlined" label={`${terms.length}`}
            sx={{ fontWeight: 700 }} />
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" color="primary"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => onAddTerm(period)}
            sx={{ fontWeight: 800, textTransform: 'none' }}>
            Add term
          </Button>
        </Stack>
        {terms.length === 0 ? (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 1.5 }}>
            <b>No terms defined.</b> The system rejects every payment, fee, collection and
            adjustment until at least one term is set up that covers the transaction date.
            Click <b>Add term</b> above to define them now.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small" sx={{
              '& .MuiTableCell-head': { bgcolor: 'grey.50', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Term</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell align="center">Current</TableCell>
                  <TableCell align="right" sx={{ width: 100 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {terms.map(t => {
                  const isNow = currentTerm?.id === t.id;
                  return (
                    <TableRow key={t.id} hover sx={{ '& td': { fontSize: 12, py: 0.85 } }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t.name}</TableCell>
                      <TableCell sx={TNUM}>{t.starts_on}</TableCell>
                      <TableCell sx={TNUM}>{t.ends_on}</TableCell>
                      <TableCell align="right" sx={TNUM}>{daysBetween(t.starts_on, t.ends_on)}</TableCell>
                      <TableCell align="center">
                        {isNow && <Chip size="small" color="success" label="Now" sx={{ fontWeight: 800, height: 20 }} />}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit term">
                          <IconButton size="small" color="primary" onClick={() => onEditTerm(t)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete term">
                          <IconButton size="small" color="error" onClick={() => onDeleteTerm(t)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ── Card-only manager (embedded inside Settings → Finance) ────
// Keep the page-level wrapper available too in case it's ever surfaced
// as a standalone screen; the SettingsPage tab consumes this Card export
// so it doesn't carry the ModuleHeader / outer max-width.
export function AccountingPeriodsCard() {
  const [periods, setPeriods] = useState([]);
  const [terms, setTerms] = useState([]); // for the active period
  const [loading, setLoading] = useState(true);

  const [periodDlg, setPeriodDlg] = useState({ open: false, editing: null });
  const [termDlg, setTermDlg]     = useState({ open: false, editing: null, period: null });

  const load = async () => {
    setLoading(true);
    try {
      const [pr, ap] = await Promise.all([
        API.get('/accounting/periods'),
        API.get('/accounting/periods/active'),
      ]);
      setPeriods(pr.data || []);
      setTerms(ap.data?.terms || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load periods')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const active = useMemo(() =>
    periods.find(p => Number(p.is_active) === 1) || null,
    [periods]);
  const others = useMemo(() =>
    periods.filter(p => Number(p.is_active) !== 1),
    [periods]);

  const savePeriod = async (form) => {
    const editing = periodDlg.editing;
    if (editing) await API.put(`/accounting/periods/${editing.id}`, form);
    else         await API.post(`/accounting/periods`, form);
    setPeriodDlg({ open: false, editing: null });
    swal.success(editing ? 'Period updated' : 'Period created');
    load();
  };

  const activatePeriod = async (p) => {
    const ok = await swal.confirm({
      title: `Activate "${p.name}"?`,
      text: `This becomes the only period accepting new transactions. The current active period will be deactivated.`,
      tone: 'primary', confirmText: 'Yes, activate',
    });
    if (!ok) return;
    try {
      await API.post(`/accounting/periods/${p.id}/activate`);
      swal.success(`"${p.name}" is now active`);
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Activate failed')); }
  };

  const lockTogglePeriod = async (p) => {
    const target = !p.locked;
    const ok = await swal.confirm({
      title: target ? `Lock "${p.name}"?` : `Unlock "${p.name}"?`,
      text: target
        ? 'New transactions will be rejected against this period until you unlock it.'
        : 'New transactions can be recorded against this period again.',
      tone: target ? 'danger' : 'primary',
      confirmText: target ? 'Yes, lock' : 'Yes, unlock',
    });
    if (!ok) return;
    try {
      await API.post(`/accounting/periods/${p.id}/${target ? 'lock' : 'unlock'}`);
      swal.success(target ? 'Locked' : 'Unlocked');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const deletePeriod = async (p) => {
    const ok = await swal.confirmDelete(`accounting period "${p.name}"`);
    if (!ok) return;
    try {
      await API.delete(`/accounting/periods/${p.id}`);
      swal.success('Removed');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const saveTerm = async (form) => {
    const { editing, period } = termDlg;
    if (editing) await API.put(`/accounting/terms/${editing.id}`, form);
    else         await API.post(`/accounting/periods/${period.id}/terms`, form);
    setTermDlg({ open: false, editing: null, period: null });
    swal.success(editing ? 'Term updated' : 'Term added');
    load();
  };

  const deleteTerm = async (t) => {
    const ok = await swal.confirmDelete(`term "${t.name}"`);
    if (!ok) return;
    try {
      await API.delete(`/accounting/terms/${t.id}`);
      swal.success('Removed');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  return (
    <Box sx={{ fontFamily: FONT_ACCT }}>
      {/* In-card header (no module header — this lives inside Settings) */}
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
        <PeriodsIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={800}>Accounting Periods</Typography>
          <Typography variant="caption" color="text.secondary">
            Bind every payment, fee, collection and adjustment to an active period and term
          </Typography>
        </Box>
        <Button variant="contained" color="primary"
          startIcon={<AddIcon />}
          onClick={() => setPeriodDlg({ open: true, editing: null })}
          sx={{ fontWeight: 800, textTransform: 'none' }}>
          New Period
        </Button>
      </Stack>

      {loading && <Card sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Card>}

      {!loading && (
        <Stack spacing={2.5}>
          <ActivePeriodCard
            period={active}
            terms={terms}
            onEdit={(p) => setPeriodDlg({ open: true, editing: p })}
            onLockToggle={lockTogglePeriod}
            onAddTerm={(p) => setTermDlg({ open: true, editing: null, period: p })}
            onEditTerm={(t) => setTermDlg({ open: true, editing: t, period: active })}
            onDeleteTerm={deleteTerm}
          />

          <ResolverTool />

          {/* Other periods */}
          <Card sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
              <CalIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>Other periods</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" variant="outlined"
                label={`${others.length} period${others.length === 1 ? '' : 's'}`}
                sx={{ fontWeight: 700 }} />
            </Box>
            {others.length === 0 ? (
              <CardContent sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                <Typography>Only the active period exists. Create another period to plan ahead.</Typography>
              </CardContent>
            ) : (
              <TableContainer>
                <Table size="small" sx={{
                  '& .MuiTableCell-head': { bgcolor: 'grey.50', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Academic Year</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell align="right">Days</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ width: 200 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {others.map(p => (
                      <TableRow key={p.id} hover sx={{ '& td': { fontSize: 12, py: 0.85 } }}>
                        <TableCell sx={{ fontWeight: 800, ...TNUM }}>
                          {p.acc_year_label || '—'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{p.name}</TableCell>
                        <TableCell sx={TNUM}>{(p.starts_on || '').slice(0, 10)}</TableCell>
                        <TableCell sx={TNUM}>{(p.ends_on || '').slice(0, 10)}</TableCell>
                        <TableCell align="right" sx={TNUM}>{daysBetween(p.starts_on, p.ends_on)}</TableCell>
                        <TableCell>
                          {p.locked
                            ? <Chip size="small" color="error" icon={<LockIcon sx={{ fontSize: 14 }} />} label="LOCKED" sx={{ fontWeight: 800, height: 20 }} />
                            : <Chip size="small" variant="outlined" label="Inactive" sx={{ fontWeight: 700, height: 20 }} />}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Activate this period">
                            <span>
                              <Button size="small" variant="contained" color="success"
                                disabled={!!p.locked}
                                onClick={() => activatePeriod(p)}
                                startIcon={<ActivateIcon fontSize="small" />}
                                sx={{ fontWeight: 700, textTransform: 'none', minWidth: 0, px: 1.4, py: 0.3, mr: 0.6 }}>
                                Activate
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary"
                              onClick={() => setPeriodDlg({ open: true, editing: p })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deletePeriod(p)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Stack>
      )}

      <PeriodDialog open={periodDlg.open}
        period={periodDlg.editing}
        onClose={() => setPeriodDlg({ open: false, editing: null })}
        onSave={savePeriod} />

      <TermDialog open={termDlg.open}
        period={termDlg.period}
        term={termDlg.editing}
        onClose={() => setTermDlg({ open: false, editing: null, period: null })}
        onSave={saveTerm} />
    </Box>
  );
}
